import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveAs } from 'file-saver';
import { FileText, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  deletePoReceivedBoxesFileRequest,
  getPoReceivedBoxesFilesRequest,
  setPoInspectorFiles,
  uploadPoReceivedBoxesFileRequest,
} from '../../slices/boxReceivedSlice';

/**
 * Compact docs list for PoInspector — upload/download/delete via boxReceivedSlice.
 */
export default function ReceivedBoxesDocsSection({ purchaseOrderId }) {
  const dispatch = useDispatch();
  const files = useSelector((state) =>
    purchaseOrderId ? state.boxReceived.inspectorByPoId[purchaseOrderId]?.files || [] : [],
  );
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const loadFiles = useCallback(async () => {
    if (!purchaseOrderId) return;
    try {
      await dispatch(getPoReceivedBoxesFilesRequest({ purchaseOrderId })).unwrap();
    } catch (error) {
      console.error('Failed to load received-boxes files', error);
      toast.error('Failed to load documents', { position: 'bottom-right' });
    }
  }, [purchaseOrderId, dispatch]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleUpload = async (acceptedFiles) => {
    if (!purchaseOrderId || !acceptedFiles?.length) return;
    setBusy(true);

    const placeholders = acceptedFiles.map((file, index) => ({
      id: `temp-${Date.now()}-${index}-${file.name}`,
      original_name: file.name,
      loading: true,
      content_type: file.type,
    }));

    dispatch(
      setPoInspectorFiles({
        purchaseOrderId,
        files: [...placeholders, ...files],
      }),
    );

    await Promise.all(
      acceptedFiles.map(async (file, index) => {
        const tempId = placeholders[index].id;
        try {
          await dispatch(
            uploadPoReceivedBoxesFileRequest({
              purchaseOrderId,
              file,
              tempId,
            }),
          ).unwrap();
        } catch (error) {
          console.error('Error uploading file:', error);
          toast.error(`Failed to upload ${file.name}`, { position: 'bottom-right' });
          await loadFiles();
        }
      }),
    );

    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDownload = async (url, fileName) => {
    try {
      const response = await fetch(url, { mode: 'cors' });
      const blob = await response.blob();
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file', { position: 'bottom-right' });
    }
  };

  const handleDelete = async (fileId) => {
    dispatch(
      setPoInspectorFiles({
        purchaseOrderId,
        files: files.map((file) =>
          file.id === fileId ? { ...file, deleting: true } : file,
        ),
      }),
    );
    try {
      await dispatch(
        deletePoReceivedBoxesFileRequest({ purchaseOrderId, fileId }),
      ).unwrap();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file', { position: 'bottom-right' });
      await loadFiles();
    }
  };

  if (!purchaseOrderId) {
    return <p className="text-xs text-gray-500">Select a PO to attach documents.</p>;
  }

  return (
    <div className="space-y-2">
      {files.length === 0 ? (
        <p className="text-xs text-gray-500">No documents attached.</p>
      ) : (
        files.map((d) => (
          <div key={d.id} className="flex items-center gap-2 text-xs">
            <FileText className="h-4 w-4 shrink-0 text-gray-400" />
            {d.loading || d.deleting ? (
              <span className="flex min-w-0 flex-1 items-center gap-1.5 text-gray-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span className="truncate">{d.original_name}</span>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => handleDownload(encodeURI(d.location_file), d.original_name)}
                className="min-w-0 flex-1 truncate text-left text-blue-600 hover:underline"
              >
                {d.original_name}
              </button>
            )}
            {!d.loading && !String(d.id).startsWith('temp-') && (
              <button
                type="button"
                disabled={d.deleting || busy}
                onClick={() => handleDelete(d.id)}
                className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
                title="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))
      )}
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => handleUpload(Array.from(e.target.files || []))}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        className="text-xs text-blue-600 hover:underline disabled:opacity-50"
      >
        + Attach document
      </button>
    </div>
  );
}
